// SCR-STAFF-403 — صورتحساب‌ها (US-BIL-020، 021، 022، 024): جستجو با شماره، وضعیت و بازه تاریخ (Cursor)، جزئیات تغییرناپذیر با لایه‌های قفل‌شده هر قلم
// و جمع‌ها، چاپ A4، A5 و حرارتی ۸۰ میلی‌متری با سربرگ شعبه (PDF با توکن) و ارسال لینک امن با پیامک. ابطال، اعلامیه بستانکاری و پرداخت‌ها (AP-BIL-02) در InvoiceCorrections.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type BilInvoiceListItem, type BilInvoiceStatus, type BilPaperSize } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, faDigits, JalaliDate, KeyValueList, ListPage, LoadingState, PageHeader, type DataTableColumn } from '@phoenix/ui';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';
import { patientPath } from '../patients/common';
import { InvoiceCorrections } from './InvoiceCorrections';
import { Amount, bilKeys, InvoiceStatusChip, invoicePath, num, saveBlob, SCR_STAFF_403, usePayerLabel, visitPath } from './common';

const STATUSES: readonly BilInvoiceStatus[] = ['Issued', 'PartiallyPaid', 'Paid', 'Voided', 'PartiallyCredited', 'FullyCredited'];
const PAPERS: readonly Exclude<BilPaperSize, null>[] = ['Thermal80', 'A5', 'A4'];

export function InvoicesPage() {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const navigate = useNavigate();
  const [number, setNumber] = useState('');
  const [status, setStatus] = useState<BilInvoiceStatus | ''>('');
  const filter = `${number.trim()}|${status}`;
  const invoices = useInfiniteQuery({
    queryKey: bilKeys.invoices(session?.tenantId, filter),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      unwrap(
        tenantApi.GET('/api/v1/billing/invoices', {
          params: { query: { number: number.trim() || undefined, status: status || undefined, cursor: pageParam, limit: 50 } },
        }),
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const columns: DataTableColumn<BilInvoiceListItem>[] = [
    { id: 'number', headerKey: 'bil:invoices.columns.number', cell: (row) => <bdi dir="ltr">{row.number}</bdi> },
    { id: 'date', headerKey: 'bil:invoices.columns.date', cell: (row) => <JalaliDate value={row.issuedAt} withTime /> },
    { id: 'patient', headerKey: 'bil:invoices.columns.patient', cell: (row) => row.patientName ?? '—' },
    { id: 'type', headerKey: 'bil:invoices.columns.type', cell: (row) => t(`bil:invoice_type.${row.type}`) },
    { id: 'payable', headerKey: 'bil:invoices.columns.payable', cell: (row) => <Amount money={row.payable} /> },
    { id: 'balance', headerKey: 'bil:invoices.columns.balance', cell: (row) => <Amount money={row.balance} /> },
    { id: 'status', headerKey: 'bil:invoices.columns.status', cell: (row) => <InvoiceStatusChip status={row.status} /> },
  ];
  return (
    <ListPage
      screenId={SCR_STAFF_403}
      titleKey="bil:invoices.title"
      subtitleKey="bil:invoices.subtitle"
      filters={
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            size="small"
            label={t('bil:invoices.search_number')}
            value={number}
            onChange={(event) => setNumber(event.target.value)}
            slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'bil-invoices-number' } }}
          />
          <TextField select size="small" label={t('bil:invoices.columns.status')} value={status} onChange={(event) => setStatus(event.target.value as BilInvoiceStatus | '')} sx={{ minWidth: 200 }}>
            <MenuItem value="">{t('bil:invoices.all')}</MenuItem>
            {STATUSES.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`bil:invoice_status.${value}`)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      }
    >
      <DataTable
        captionKey="bil:invoices.title"
        columns={columns}
        rows={invoices.data?.pages.flatMap((page) => page.items)}
        getRowId={(row) => row.id}
        isLoading={invoices.isPending}
        error={invoices.error}
        onRetry={() => void invoices.refetch()}
        hasMore={invoices.hasNextPage}
        onLoadMore={() => void invoices.fetchNextPage()}
        isFetchingMore={invoices.isFetchingNextPage}
        emptyTitleKey="bil:invoices.empty"
        onRowClick={(row) => void navigate(invoicePath(row.id))}
      />
    </ListPage>
  );
}

export function InvoicePage() {
  const { t } = useTranslation('bil');
  const { id = '' } = useParams();
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const payerLabel = usePayerLabel();
  const sendKey = useIdempotencyKey();
  const [paper, setPaper] = useState<Exclude<BilPaperSize, null>>('Thermal80');
  const [printError, setPrintError] = useState<unknown>();
  const [printing, setPrinting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const query = useQuery({
    queryKey: bilKeys.invoice(session?.tenantId, id),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/invoices/{id}', { params: { path: { id } } })),
    enabled: id !== '',
  });
  const send = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/billing/invoices/{id}:send', {
          params: { path: { id } },
          headers: { 'Idempotency-Key': sendKey.current() },
        }),
      ),
    onSettled: (_data, error) => sendKey.settle(error ?? undefined),
    onSuccess: () => {
      setToast(t('bil:invoice.sent'));
      void query.refetch();
    },
  });
  const invoice = query.data;

  const print = async () => {
    if (!invoice) {
      return;
    }
    setPrinting(true);
    setPrintError(undefined);
    const response = await tenantApi.GET('/api/v1/billing/invoices/{id}/print', {
      params: { path: { id }, query: { format: paper, output: 'Pdf' } },
      parseAs: 'blob',
    });
    setPrinting(false);
    if (response.response.ok && response.data) {
      saveBlob(response.data as unknown as Blob, `${invoice.number}-${paper}.pdf`);
      setToast(t('bil:invoice.printed', { paper: t(`bil:paper.${paper}`) }));
    } else {
      setPrintError(response.error ?? new Error('print'));
    }
  };

  if (query.isPending) {
    return <LoadingState rows={6} />;
  }
  if (query.error || !invoice) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }
  const canSend = access.accessMode === 'Full' && access.can('bil.invoice.issue');

  return (
    <Stack data-screen-id={SCR_STAFF_403} spacing={2}>
      <PageHeader
        titleKey="bil:invoice.title"
        titleValues={{ number: invoice.number }}
        badges={<InvoiceStatusChip status={invoice.status} />}
        actions={
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <TextField
              select
              size="small"
              label={t('bil:invoice.paper')}
              value={paper}
              onChange={(event) => setPaper(event.target.value as Exclude<BilPaperSize, null>)}
              sx={{ minWidth: 160 }}
              slotProps={{ htmlInput: { 'data-testid': 'bil-print-paper' } }}
            >
              {PAPERS.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`bil:paper.${value}`)}
                </MenuItem>
              ))}
            </TextField>
            <Button variant="contained" onClick={() => void print()} disabled={printing} data-testid="bil-print">
              {t('bil:invoice.print')}
            </Button>
            {canSend && (
              <Button variant="outlined" onClick={() => send.mutate()} disabled={send.isPending} data-testid="bil-send">
                {t('bil:invoice.send')}
              </Button>
            )}
          </Stack>
        }
      />
      {printError ? <ErrorState error={printError} compact /> : null}
      {send.error ? <ErrorState error={send.error} compact /> : null}
      {invoice.isProvisional && <Alert severity="info">{t('bil:invoice.provisional')}</Alert>}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <KeyValueList
          items={[
            { labelKey: 'bil:invoice.number', value: <bdi dir="ltr" data-testid="bil-invoice-number">{invoice.number}</bdi> },
            { labelKey: 'bil:invoice.type', value: t(`bil:invoice_type.${invoice.type}`) },
            { labelKey: 'bil:invoice.issued_at', value: <JalaliDate value={invoice.issuedAt} withTime /> },
            { labelKey: 'bil:invoice.fiscal_year', value: faDigits(num(invoice.fiscalYear)) },
            {
              labelKey: 'bil:invoice.patient',
              value: access.can('pat.patient.view') ? (
                <Link component={RouterLink} to={patientPath(invoice.patientId)}>
                  {invoice.patientName ?? '—'}
                </Link>
              ) : (
                (invoice.patientName ?? '—')
              ),
            },
            { labelKey: 'bil:invoice.payer', value: payerLabel(invoice.payer) },
            {
              labelKey: 'bil:invoice.visit',
              value: invoice.visitId ? (
                <Link component={RouterLink} to={visitPath(invoice.visitId)}>
                  {t('bil:invoice.open_visit')}
                </Link>
              ) : (
                '—'
              ),
            },
            { labelKey: 'bil:invoice.sent_at', value: invoice.sentAt ? <JalaliDate value={invoice.sentAt} withTime /> : '—' },
          ]}
        />
      </Paper>
      <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small" aria-label={t('bil:invoice.lines')}>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>{t('bil:lines.service')}</TableCell>
              <TableCell>{t('bil:lines.quantity')}</TableCell>
              <TableCell>{t('bil:lines.unit_price')}</TableCell>
              <TableCell>{t('bil:totals.gross')}</TableCell>
              <TableCell>{t('bil:totals.discount')}</TableCell>
              <TableCell>{t('bil:totals.tax')}</TableCell>
              <TableCell>{t('bil:lines.payable')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoice.lines.map((line) => (
              <TableRow key={line.id} data-testid="bil-invoice-line">
                <TableCell>{faDigits(num(line.lineNo))}</TableCell>
                <TableCell>{line.title}</TableCell>
                <TableCell>{faDigits(num(line.quantity))}</TableCell>
                <TableCell>
                  <Amount money={line.unitPrice} />
                </TableCell>
                <TableCell>
                  <Amount money={line.gross} />
                </TableCell>
                <TableCell>
                  <Amount money={{ amount: line.automaticDiscount.amount + line.manualDiscount.amount, currency: line.gross.currency }} />
                </TableCell>
                <TableCell>
                  <Amount money={line.tax} />
                  {num(line.taxRateBasisPoints) > 0 ? (
                    <Typography variant="caption" color="text.secondary" component="div">
                      {t('bil:invoice.tax_rate', { rate: faDigits((num(line.taxRateBasisPoints) / 100).toString()) })}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>
                  <Amount money={line.patientPayable} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      <Paper variant="outlined" sx={{ p: 2 }} data-testid="bil-invoice-totals">
        <KeyValueList
          items={[
            { labelKey: 'bil:totals.gross', value: <Amount money={invoice.totals.gross} /> },
            { labelKey: 'bil:totals.insurance', value: <Amount money={invoice.totals.insuranceShare} /> },
            { labelKey: 'bil:totals.discount', value: <Amount money={invoice.totals.discount} /> },
            { labelKey: 'bil:totals.tax', value: <Amount money={invoice.totals.tax} /> },
            { labelKey: 'bil:totals.rounding', value: <Amount money={invoice.totals.rounding} /> },
            { labelKey: 'bil:totals.payable', value: <Amount money={invoice.totals.payable} testId="bil-invoice-payable" /> },
            { labelKey: 'bil:invoice.paid', value: <Amount money={invoice.paid} /> },
            { labelKey: 'bil:invoice.balance', value: <Amount money={invoice.balance} /> },
          ]}
        />
      </Paper>
      <InvoiceCorrections invoice={invoice} onChanged={() => void query.refetch()} />
      <Snackbar open={toast !== null} autoHideDuration={5000} onClose={() => setToast(null)} message={toast ?? ''} />
    </Stack>
  );
}
